import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import 'moment/locale/vi';
moment.locale('vi');
import {
  Button,
  FormGroup,
  Label,
  Input,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  AttendanceContainer,
  ActionBar,
  StyledTable,
  ModalContent,
  ButtonStyled,
  FilterForm,
  FormGroupStyled,
  ScrollableTableWrapper,
  StyledModal,
  TableContainer,
  CreateButton,
  Select,
  InfoButton,
  DangerButton,
  PrimaryButton,
  SecondaryButton,
  SuccessButton,
} from './AttendanceManage.styled';
import { studentActions, classActions, attendanceActions, userActions, scheduleActions } from '../../redux/actions';
import { studentService } from '../../services/student.service';
import { attendanceStudentRecordActions } from '../../redux/actions/attendanceStudentRecord.actions';
import { pathConstants } from '../../constants';
import { useNavigate } from 'react-router-dom';
import Loading from '../../common/Loading/loading';
import ConfirmationPopup from '../Common/ConfirmationPopup';
import StatusBar from '../Common/StatusBar';
import { checkMissingAttendance } from './CheckMissingAttendance';
import { AttendanceAlertModal, AttendanceAlertType } from './AttendanceAlertModal';
import { checkDuplicatingAttendance } from './CheckDuplicatingAttendance';
import classMonthlySessionService from '../../services/classMonthlySession.service';

interface StudentAttributes {
  id: number;
  name: string;
  dateOfBirth?: Date;
  phoneNumber?: string;
  fbLink?: string;
  note?: string;
  classId: number;
  startDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}
interface AttendanceSessionAttributes {
  id: number;
  classId: number;
  date: Date;
  createdByTeacherId: number;
  lastUpdatedByTeacherId: number;
  note?: string;
  createdAt: Date;
  updatedAt: Date;
}

type Alert = {
  teacherName: string;
  messages: string[];
  attendanceAlertType: AttendanceAlertType;
};

const AttendanceManage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [isCreatingAttendance, setIsCreatingAttendance] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchText, setSearchText] = useState('');
  const [attendanceSessionNote, setAttendanceSessionNote] = useState('');
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<any>(null);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [isEditing, setIsEditing] = useState(false);  // Trạng thái modal: sửa hay tạo mới
  const [filteredStudents, setFilteredStudents] = useState<StudentAttributes[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    moment().tz('Asia/Ho_Chi_Minh').startOf('day').format('YYYY-MM-DD')
  );  
  const [attendanceSessionToEdit, setAttendanceSessionToEdit] = useState<AttendanceSessionAttributes | null>(null);
  const [isDuplicateSession, setIsDuplicateSession] = useState(false);
  const [errorDuplicatedSessionMessage, setErrorDuplicatedSessionMessage] = useState('');
  // Thông báo khi lớp không có lịch học vào ngày được chọn
  const [isScheduleMismatch, setIsScheduleMismatch] = useState(false);
  const [scheduleMismatchMessage, setScheduleMismatchMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true); // Thêm trạng thái loading
  const [showConfirmNewStudents, setShowConfirmNewStudents] = useState(false);
  const [newStudentsList, setNewStudentsList] = useState<StudentAttributes[]>([]);

  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [confirmedSessions, setConfirmedSessions] = useState<any[]>([]);
  const classes = useAppSelector((state: any) => state.classes.classesList);
  const students = useAppSelector((state: any) => state.students.studentsList);
  const teachers = useAppSelector((state: any) => state.users.items);
  const schedulesList = useAppSelector((state: any) => state.schedules.schedulesList);
  const attendanceSessions = useAppSelector((state: any) => state.attendanceSessions.attendanceSessionsList);
  const currentMonthAttendanceStudentRecords = useAppSelector((state: any) => state.attendanceStudentRecords.attendanceStudentRecordsList);
  const [archivedStudents, setArchivedStudents] = useState<any[]>([]); // học sinh đã nghỉ
  
  // Tạo danh sách các năm (năm hiện tại ± 2)
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Hàm kiểm tra trùng lặp
  const checkDuplicateSession = (classId: string, date: string) => {
    const duplicateSession = attendanceSessions.some(
      (session: AttendanceSessionAttributes) =>
        session.classId === Number(classId) && moment(session.date).format('YYYY-MM-DD') === date
    );

    if (duplicateSession) {
      const className = classes.find((cls: any) => cls.id === Number(classId))?.name || '';
      setErrorDuplicatedSessionMessage(`Đã tồn tại điểm danh rồi: lớp ${className} - ngày ${date}`);
      setIsDuplicateSession(true);
    } else {
      setErrorDuplicatedSessionMessage('');
      setIsDuplicateSession(false);
    }
  };

  // Helper: map giá trị dayOfWeek (2..8) => tên tiếng Việt
  const scheduleDayName = (val: number) => {
    const map: Record<number, string> = {
      2: 'Thứ Hai',
      3: 'Thứ Ba',
      4: 'Thứ Tư',
      5: 'Thứ Năm',
      6: 'Thứ Sáu',
      7: 'Thứ Bảy',
      8: 'Chủ Nhật',
    };
    return map[val] || `Thứ ${val - 1}`;
  };

  // Hàm kiểm tra xem lớp có lịch học vào ngày 'date' hay không
  const checkClassSchedule = (classId: string, date: string) => {
    // Lấy tất cả lịch của lớp
    const classSchedules = Array.isArray(schedulesList)
      ? schedulesList.filter((s: any) => s.classId === Number(classId))
      : [];

    // Nếu không có lịch nào => mismatch
    if (!classSchedules.length) {
      const className = classes.find((cls: any) => cls.id === Number(classId))?.name || '';
      setScheduleMismatchMessage(
        `Lớp ${className} chưa có lịch học vào ngày ${moment(date).locale('vi').format('dddd - DD/MM/YYYY')}.`
      );
      setIsScheduleMismatch(true);
      return;
    }

    // selected date -> iso weekday 1..7 (1 = Monday)
    const selectedIso = moment(date).isoWeekday(); // 1..7

    // schedulesList dùng 2..8 (2 = Thứ Hai, 8 = Chủ Nhật)
    const hasScheduleToday = classSchedules.some((s: any) => Number(s.dayOfWeek) - 1 === selectedIso);

    if (!hasScheduleToday) {
      const className = classes.find((cls: any) => cls.id === Number(classId))?.name || '';
      // Tạo chuỗi các ngày trong tuần mà lớp có lịch, ví dụ "Thứ Ba, Thứ Sáu"
      const uniqueDays = Array.from(new Set(classSchedules.map((s: any) => Number(s.dayOfWeek))));
      const dayNames = uniqueDays.map((d) => scheduleDayName(d)).join(', ');
      setScheduleMismatchMessage(
        `Lớp ${className} (${dayNames}) không có lịch học ngày hôm nay (${moment(date).locale('vi').format('dddd - DD/MM/YYYY')}).`
      );
      // Ví dụ hiển thị: "Lớp 3A (Thứ Ba, Thứ Sáu) không có lịch học ngày hôm nay (Thứ Ba - 22/07/2025)."
      setIsScheduleMismatch(true);
    } else {
      setScheduleMismatchMessage('');
      setIsScheduleMismatch(false);
    }
  };

  // Hàm logout
  const onLogout = () => {
    dispatch(userActions.logout());
    navigate(pathConstants.ROOT);
  };

  const calculateAttendanceSummary = () => {
    const validStudents = isEditing ? filteredStudents.filter((student) => moment(student.startDate).format('YYYY-MM-DD') <= moment(selectedDate).format('YYYY-MM-DD')|| !student.startDate) : filteredStudents; // ở modal sửa: loại bỏ các học sinh mà chưa nhập học vào ngày điểm danh này.
    const totalStudents =  validStudents.length;
    const presentStudents = selectedStudents.length;
    const absentStudents = validStudents
      .filter((student) => !selectedStudents.includes(student.id))
      .map((student) => student.name); // Lấy tên học sinh vắng
    
    return {
      totalStudents,
      presentStudents,
      absentStudents,
    };
  };

  // Kiểm tra accessToken hết hạn
  const isTokenExpired = (token: string | null) => {
    if (!token) return true; // Nếu không có token, coi như đã hết hạn
    try {
      const payloadBase64 = token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payloadBase64)); // Decode JWT payload
      const currentTime = Date.now() / 1000; // Thời gian hiện tại (tính bằng giây)
      return decodedPayload.exp < currentTime; // Token hết hạn nếu `exp` nhỏ hơn thời gian hiện tại
    } catch (error) {
      console.error('Invalid token:', error);
      return true; // Nếu token không hợp lệ, coi như đã hết hạn
    }
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (isTokenExpired(accessToken)) {
      console.log('Token đã hết hạn, thực hiện logout...');
      onLogout();
    }
  }, []); // useEffect chỉ chạy một lần khi component được mount

  useEffect(() => {// Gọi hàm kiểm tra khi thay đổi lớp hoặc ngày
    if (selectedClass && selectedDate) {
      checkDuplicateSession(selectedClass, selectedDate);
      checkClassSchedule(selectedClass, selectedDate);
    }
  }, [selectedClass, selectedDate]);

  // Kiểm tra trùng lặp & lịch học ngay khi modal mở lên (nếu có selectedClass và selectedDate)
  useEffect(() => {
    if (modalOpen && selectedClass && selectedDate) {
      checkDuplicateSession(selectedClass, selectedDate);
      checkClassSchedule(selectedClass, selectedDate);
    } else if (!modalOpen) {
      // Reset các cảnh báo khi modal đóng
      setIsDuplicateSession(false);
      setIsScheduleMismatch(false);
      setScheduleMismatchMessage('');
      setErrorDuplicatedSessionMessage('');
    }
  }, [modalOpen]);

  useEffect(() => {
    // Đánh dấu bắt đầu tải dữ liệu
    setIsLoading(true);
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    Promise.all([
      dispatch(classActions.getAll()),
      dispatch(studentActions.getAll()),
      dispatch(userActions.getAllTeachers()),
      dispatch(attendanceActions.getAllAttendanceSessions()),
      dispatch(scheduleActions.getAll()),
      dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordByMonth(currentYear, currentMonth))
    ])
    .then(() => {
      setIsLoading(false); // Kết thúc tải dữ liệu
      setDataLoaded(true); // <-- Đánh dấu dữ liệu đã sẵn sàng
    })
    .catch((err) => {
      console.error('Error loading data:', err);
      setIsLoading(false);
    });

    // Load archived students để hiện trong modal sửa điểm danh quá khứ
    studentService.getArchived().then((res: any) => {
      setArchivedStudents(Array.isArray(res) ? res : []);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch confirmed sessions (current month + previous month) for missing attendance check
  useEffect(() => {
    const fetchConfirmedSessions = async () => {
      try {
        const today = new Date();
        const currM = today.getMonth() + 1;
        const currY = today.getFullYear();
        const prevDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prevM = prevDate.getMonth() + 1;
        const prevY = prevDate.getFullYear();
        const [curr, prev] = await Promise.all([
          classMonthlySessionService.getAll({ month: currM, year: currY }),
          classMonthlySessionService.getAll({ month: prevM, year: prevY }),
        ]);
        const all = [
          ...(curr?.data || []),
          ...(prev?.data || []),
        ].filter((s: any) => s.isActive && s.confirmedAt);
        setConfirmedSessions(all);
      } catch (e) {
        console.error('[AttendanceManage] Failed to fetch confirmed sessions:', e);
      }
    };
    fetchConfirmedSessions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Chỉ chạy khi dataLoaded là true và các state đã sẵn sàng
  useEffect(() => {
    // Chỉ chạy khi dữ liệu đã load xong VÀ các array không rỗng
    if (!dataLoaded) return;
    if (!classes.length || !confirmedSessions.length || !attendanceSessions.length || !teachers.length) return;

    // Nếu đang tạo/sửa điểm danh thì không hiển thị các modal cảnh báo
    if (isCreatingAttendance) return;

    const missingAttendaceAlerts: any = checkMissingAttendance(
      confirmedSessions,
      attendanceSessions,
      classes,
      teachers
    );
    const duplicatingAttendanceAlerts: any = checkDuplicatingAttendance(
      attendanceSessions,
      classes,
      teachers
    );
    if (missingAttendaceAlerts.length > 0 || duplicatingAttendanceAlerts.length > 0) {
      setAlerts([
...missingAttendaceAlerts,
...duplicatingAttendanceAlerts,
]);
      setShowModal(true);
    }
  }, [dataLoaded, classes, confirmedSessions, attendanceSessions, teachers, isCreatingAttendance]);

  // useEffect mới để reset selectedStudents khi lớp thay đổi
  useEffect(() => {
    if (selectedClass) {
      let baseStudents = students.filter((student: StudentAttributes) => student.classId === Number(selectedClass));

      if (isEditing && attendanceSessionToEdit) {
        // Lấy danh sách học sinh được chọn từ attendanceSessionToEdit (chỉ khi đang sửa)
        const studentsInSession = currentMonthAttendanceStudentRecords ? currentMonthAttendanceStudentRecords.filter(
          (record: any) => record.sessionId === attendanceSessionToEdit.id
        ): [];

        const selectedStudentIds = studentsInSession
          .filter((record: any) => record.isPresent === true)
          .map((record: any) => record.studentId);

        setSelectedStudents(selectedStudentIds); // Cập nhật danh sách học sinh có mặt

        // Thêm học sinh đã nghỉ có record trong buổi này vào danh sách
        const activeIds = new Set(baseStudents.map((s: any) => s.id));
        const archivedInSession = studentsInSession
          .map((r: any) => archivedStudents.find((s: any) => s.id === r.studentId))
          .filter((s: any) => s && !activeIds.has(s.id) && s.classId === Number(selectedClass));
        baseStudents = [...baseStudents, ...archivedInSession];
      } else {
        // Reset khi tạo mới
        setSelectedStudents([]);
      }

      setFilteredStudents(baseStudents);
    } else {
      setFilteredStudents([]);
      setSelectedStudents([]); // Reset nếu không có lớp
    }
  }, [selectedClass, students, archivedStudents, isEditing, attendanceSessionToEdit, currentMonthAttendanceStudentRecords]);
  
  const filteredSessions = 
  Array.isArray(attendanceSessions) && attendanceSessions.length > 0 ? 
  attendanceSessions.filter((session: AttendanceSessionAttributes) => {
    const sessionMonth = new Date(session.date).getMonth() + 1;
    const sessionYear = new Date(session.date).getFullYear();
    const isClassMatch = selectedClass ? session.classId === Number(selectedClass) : true;
    const isMonthMatch = sessionMonth === selectedMonth;
    const isYearMatch = sessionYear === selectedYear;

    return isClassMatch && isMonthMatch && isYearMatch;
  })
  .sort((a: AttendanceSessionAttributes, b: AttendanceSessionAttributes) => {
    const today = new Date();
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);

    const diffA = Math.abs(dateA.getTime() - today.getTime());
    const diffB = Math.abs(dateB.getTime() - today.getTime());

    // Nếu `date` khác nhau, sắp xếp theo khoảng cách thời gian đến ngày hiện tại
    if (diffA !== diffB) {
      return diffA - diffB;
    }

    // Nếu `date` giống nhau, sắp xếp theo `createdAt` giảm dần
    const createdAtA = new Date(a.createdAt).getTime();
    const createdAtB = new Date(b.createdAt).getTime();
    return createdAtB - createdAtA;
  }) : [];


  const resetModalState = () => {
    // setSelectedClass('');
    setErrorDuplicatedSessionMessage('');
    setAttendanceSessionNote('');
    setErrorDuplicatedSessionMessage('');
    setIsEditing(false);
    setIsDuplicateSession(false);
    setIsScheduleMismatch(false);
    setScheduleMismatchMessage('');
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedStudents([]);
    setAttendanceSessionToEdit(null);  // Reset thông tin điểm danh đang sửa
  };
  
  const toggleModal = () => {
    resetModalState();  // Reset thông tin khi đóng modal
    setModalOpen(!modalOpen);
  };

  const handleCreateAttendance = async () => {
    if (isCreatingAttendance) return; // chặn double submit
    setIsEditing(false);  // Chế độ tạo mới điểm danh
    setIsCreatingAttendance(true);
    
     // Lưu lại ngày điểm danh hiện tại trước khi có bất kỳ thay đổi nào
     const attendanceDateForNewStarts = selectedDate;

    // Lấy danh sách học sinh và trạng thái checkbox
    const attendanceSessionListStudentList = filteredStudents
      .filter((student: StudentAttributes) => {
        // Nếu student.startDate là null, chỉ giữ lại học sinh có mặt (isPresent == 1, tức là có trong selectedStudents).
        if (!student.startDate) return selectedStudents.includes(student.id);

        // Nếu có startDate, giữ lại nếu ngày đó nhỏ hơn hoặc bằng selectedDate.
        return (
          moment(student.startDate).format('YYYY-MM-DD') <= moment(selectedDate).format('YYYY-MM-DD')
        );
      })
      .map((student) => ({
        studentId: student.id,
        isPresent: selectedStudents.includes(student.id) ? 1 : 0, // true -> 1, false -> 0
        startDate: student.startDate, // Thêm startDate để lọc
      }));

    try {
      // 1️⃣ Dispatch action tạo điểm danh TRƯỚC
      await dispatch(
        attendanceActions.createAttendanceSession(
          Number(selectedClass), // classId
          selectedDate, // date
          attendanceSessionNote, //note
          attendanceSessionListStudentList, // danh sách học sinh,
          userInfo.id,
          userInfo.id
        )
      );

      // 2️⃣ Lọc danh sách học sinh cần cập nhật startDate, là các học sinh đi học buổi đầu (tức học sinh có startDate=null && isPresent=1)
      const newStartStudents = filteredStudents.filter(
        (student) => !student.startDate && selectedStudents.includes(student.id)
      );

      // 3️⃣ Dispatch cập nhật tất cả học sinh cùng lúc (parallel, không tuần tự) để tăng tốc độ
      if (newStartStudents.length > 0) {
        await Promise.all(
          newStartStudents.map((student) => {
            const updatedFormData = {
              name: student.name,
              dateOfBirth: student.dateOfBirth,
              phoneNumber: student.phoneNumber,
              fbLink: student.fbLink,
              note: student.note,
              classId: student.classId,
              startDate: attendanceDateForNewStarts, // dùng ngày đã lưu
              id: student.id,
            };
            return dispatch(studentActions.updateStudent(updatedFormData));
          })
        );
      }

      // Đóng modal sau khi hoàn thành (sau khi cập nhật học sinh)
      toggleModal();
    } catch (error) {
      console.error("Lỗi khi tạo điểm danh hoặc cập nhật học sinh:", error);
    } finally {
      setIsCreatingAttendance(false);
    }
   };


  const handleEditAttendance = (session: AttendanceSessionAttributes) => {
    setIsEditing(true);  // Chế độ sửa
    // Khi ấn sửa, điền sẵn thông tin vào modal
    setAttendanceSessionToEdit(session);
    setSelectedClass(String(session.classId));
    setAttendanceSessionNote(session.note || '');
    setSelectedDate(new Date(session.date).toISOString().split('T')[0]);

    // Lọc ra những học sinh đã có mặt trong session này
    const studentsInSession = currentMonthAttendanceStudentRecords ? currentMonthAttendanceStudentRecords.filter(
      (record: any) => record.sessionId === session.id
    ) : [];
  
    // Tạo danh sách học sinh có mặt (dựa trên trường isPresent)
    const selectedStudentIds = studentsInSession
      .filter((record: any) => record.isPresent === true) // Chọn những học sinh có mặt
      .map((record: any) => record.studentId);
    setSelectedStudents(selectedStudentIds);
    setModalOpen(true);
  };  

  const handleDeleteAttendance = (session: AttendanceSessionAttributes) => {
    dispatch(attendanceActions.deleteAttendanceSession(session.id));
    setShowDeletePopup(false);
  }

  const handleSaveAttendance = async () => {
    if (isCreatingAttendance) return; // chặn double submit
    setIsCreatingAttendance(true);
    // Lưu lại ngày điểm danh hiện tại trước khi thay đổi/modal reset
    const attendanceDateForNewStarts = selectedDate;

    // Lưu lại điểm danh, xử lý logic lưu sửa điểm danh tại đây
    if (attendanceSessionToEdit) {
      const updatedAttendance = filteredStudents ? filteredStudents
      .map((student) => ({
        studentId: student.id,
        isPresent: selectedStudents.includes(student.id) ? 1 : 0,
        startDate: student.startDate, // Thêm startDate để lọc
      }))
      .filter((student) => (!student.startDate && student.isPresent == 1) || (moment(student.startDate).format('YYYY-MM-DD') <= moment(selectedDate).format('YYYY-MM-DD'))) // loại (tức không cần sửa điểm danh) cho những Hsinh mới, mà chưa bắt đầu đi học (startDate=null) và cũng chưa đi buổi này (isPresent=0); và những học sinh nhập học sau ngày điểm danh này.
      : [];

      try {
        // Dispatch action sửa điểm danh
        await dispatch(
          attendanceActions.updateAttendanceSession(
            attendanceSessionToEdit.id,
            selectedDate,
            attendanceSessionNote,
            updatedAttendance,
            userInfo.id
          )
        );

        // 2️⃣ Lọc danh sách học sinh cần cập nhật startDate, là các học sinh đi học buổi đầu (tức học sinh có startDate=null && isPresent=1)
        const newStartStudents = filteredStudents.filter(
          (student) => !student.startDate && selectedStudents.includes(student.id)
        );

        // 3️⃣ Dispatch cập nhật tất cả học sinh cùng lúc (parallel, không tuần tự) để tăng tốc độ
        if (newStartStudents.length > 0) {
          await Promise.all(
            newStartStudents.map((student) => {
              const updatedFormData = {
                name: student.name,
                dateOfBirth: student.dateOfBirth,
                phoneNumber: student.phoneNumber,
                fbLink: student.fbLink,
                note: student.note,
                classId: student.classId,
                startDate: attendanceDateForNewStarts, // dùng ngày đã lưu
                id: student.id,
              };
              return dispatch(studentActions.updateStudent(updatedFormData));
            })
          );
        }

        // Đóng modal sau khi cập nhật xong
        toggleModal();
      } catch (error) {
        console.error("Lỗi khi sửa điểm danh hoặc cập nhật học sinh:", error);
      } finally {
        setIsCreatingAttendance(false);
      }
    }
    // Redux store đã được cập nhật đúng bởi UPDATE_NEW_STUDENT_RECORDS_SUCCESS
    // → không cần fetch lại (fetch lại sẽ ghi đè bằng data tháng hiện tại, gây mất data khi xem tháng khác)
  };
  const handleCheckNewStudents = () => {
    const newStudents = filteredStudents.filter(student => !student.startDate && selectedStudents.includes(student.id)); //hsinh mới và được checked điểm danh
    if (newStudents.length > 0) {
      setNewStudentsList(newStudents);
      setShowConfirmNewStudents(true);
    } else {
      handleCreateAttendance();
    }
  };

  // 👉 Tính tổng tất cả lượt đi học và vắng mặt trong tất cả filteredSessions
  let totalPresent = 0;
  let totalAbsent = 0;

  if (Array.isArray(filteredSessions) && filteredSessions.length > 0) {
    filteredSessions.forEach((session: any) => {
      const sessionRecords = currentMonthAttendanceStudentRecords.filter(
        (record: any) => record.sessionId === session.id
      );

      sessionRecords.forEach((record: any) => {
        if (record.isPresent === true) totalPresent += 1;
        else totalAbsent += 1;
      });
    });
  }

  return (
    <AttendanceContainer>
      {/* Loại bỏ loading spinner - chỉ dùng FadeIn animation */}
      <AttendanceAlertModal
        show={showModal}
        alerts={alerts}
        onClose={() => setShowModal(false)}
        onCreateAttendance={(classId: number, date: string) => {
          setShowModal(false);
          resetModalState();
          setSelectedClass(String(classId));
          setSelectedDate(date);
          setIsEditing(false);
          setModalOpen(true);
        }}
      />
      <ActionBar className="action-bar">
        <CreateButton onClick={toggleModal}>
          + Tạo Điểm Danh
        </CreateButton>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Tháng</span>
          <Select
            value={selectedMonth}
            onChange={(e) => {
              const newMonth = Number(e.target.value);
              setSelectedMonth(newMonth);
              dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordByMonth(selectedYear, newMonth));
            }}
            style={{ width: '80px' }}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </Select>
          
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Năm</span>
          <Select
            value={selectedYear}
            onChange={(e) => {
              const newYear = Number(e.target.value);
              setSelectedYear(newYear);
              dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordByMonth(newYear, selectedMonth));
            }}
            style={{ width: '100px' }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
          
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Lớp</span>
          <Select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            style={{ width: '150px' }}
          >
            <option value="">Tất cả</option>
            {classes
              ? [...classes]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((cls: any) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))
              : []}
          </Select>
        </div>
      </ActionBar>
      <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th>ID</th>
            <th>Lớp</th>
            <th>Ngày điểm danh</th>
            <th>Đi/Vắng</th>
            <th>Ghi chú</th>
            <th>Chỉnh sửa</th>
          </tr>
        </thead>
        <tbody>
          {filteredSessions.map((session: AttendanceSessionAttributes) => {
            const className = classes ? (classes.find((cls: any) => cls.id === session.classId)?.name || 'N/A') : [];
            const createdTeacherName = teachers ? (teachers.find((teacher: any) => teacher.id === session.createdByTeacherId)?.userName || 'N/A') : [];
            const lastUpdatedTeacherName = teachers ? (teachers.find((teacher: any) => teacher.id === session.lastUpdatedByTeacherId)?.userName || 'N/A') : [];
            const formattedDate = moment(session.date).locale('vi').format('dddd - DD/MM/YYYY');


            return (
              <tr key={session.id}>
                <td>{session.id}</td>
                <td>{className}</td> {/* Hiển thị tên lớp */}
                <td>{formattedDate}</td> {/* Hiển thị ngày định dạng dd/mm/yyyy */}
                <td>
                  {(() => {
                    const sessionRecords = currentMonthAttendanceStudentRecords
                      ? currentMonthAttendanceStudentRecords.filter(
                          (record: any) => record.sessionId === session.id
                        )
                      : [];

                    const totalStudents = sessionRecords.length;
                    const presentStudentRecords = sessionRecords.filter(
                      (record: any) => record.isPresent
                    );

                    const presentStudentNames = presentStudentRecords
                      .map((record: any) =>
                        students
                          ? students.find((student: any) => student.id === record.studentId)?.name
                          : ''
                      )
                      .filter(Boolean);

                    const absentRecords = sessionRecords.filter(
                      (record: any) => !record.isPresent
                    );

                    const absentStudentNames = absentRecords
                      .map((record: any) =>
                        students
                          ? students.find((student: any) => student.id === record.studentId)?.name
                          : ''
                      )
                      .filter(Boolean);

                    // Chia danh sách học sinh vắng thành các nhóm, mỗi nhóm tối đa 3 học sinh
                    const absentStudentGroups = absentStudentNames.reduce(
                      (groups: string[][], name: string, index: number) => {
                        const groupIndex = Math.floor(index / 3);
                        if (!groups[groupIndex]) groups[groupIndex] = [];
                        groups[groupIndex].push(name);
                        return groups;
                      },
                      []
                    );

                    const handleCopyPresentStudents = () => {
                      const textToCopy = presentStudentNames.join('\n');
                      navigator.clipboard
                        .writeText(textToCopy)
                        .catch((err) => {
                          console.error('Lỗi khi copy:', err);
                        });
                    };
                    return (
                      <>
                      <div
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={handleCopyPresentStudents}
                        title="Nhấn để copy danh sách học sinh đi học"
                      >
                        {`Đi: ${presentStudentRecords.length}/${totalStudents}; Vắng: ${
                          totalStudents - presentStudentRecords.length
                        }`}
                      </div>
                        {absentStudentGroups.length > 0 && (
                          <div style={{ color: 'red', fontSize: '12px' }}>
                            {absentStudentGroups.map((group: any[], index: React.Key | null | undefined) => (
                              <div key={index}>{group.join('; ')}</div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </td>
                <td>
                  <div style={{ color: 'green', fontSize: '12px' }}>
                    {session.note || ''}
                  </div>
                </td>
                <td>
                  <div>
                    <InfoButton onClick={() => handleEditAttendance(session)}>
                      SỬA
                    </InfoButton>
                    {' '}
                    <DangerButton onClick={() => {
                      setSessionToDelete(session);
                      setShowDeletePopup(true);
                    }}>
                      XOÁ
                    </DangerButton>
                  </div>
                  {(() => {
                    const isEdited = session.createdAt && session.updatedAt && 
                      new Date(session.createdAt).getTime() !== new Date(session.updatedAt).getTime();
                    
                    const formatDateTime = (date: Date) => {
                      return new Date(date).toLocaleString('vi-VN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                    };

                    return (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                        <div>
                          <strong>Tạo:</strong> {createdTeacherName}
                          {session.createdAt && ` (${formatDateTime(session.createdAt)})`}
                        </div>
                        {isEdited && (
                          <div style={{ marginTop: '4px' }}>
                            <strong>Sửa:</strong> {lastUpdatedTeacherName}
                            {session.updatedAt && ` (${formatDateTime(session.updatedAt)})`}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
      </TableContainer>
      {userInfo?.role == 0 && (
        <div style={{ marginTop: '10px', fontWeight: '500' }}>
          <p>
            Total buổi: {filteredSessions.length} &nbsp;|&nbsp;
            Total đi học: <span style={{ color: 'green' }}>{totalPresent}</span> &nbsp;|&nbsp;
            Total vắng: <span style={{ color: 'red' }}>{totalAbsent}</span>
          </p>
        </div>
      )}

      <StyledModal isOpen={modalOpen} toggle={() => !isCreatingAttendance && toggleModal()}>
        <ModalHeader toggle={() => !isCreatingAttendance && toggleModal()}>
           {attendanceSessionToEdit ? 'Sửa Điểm Danh' : 'Tạo Điểm Danh'}
         </ModalHeader>
        <ModalBody>
          <ModalContent>
            {/* Căn chỉnh hai trường "Lớp" và "Điểm danh ngày" */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', justifyContent: 'flex-start' }}>
              {/* Trường Lớp */}
              <FormGroup style={{ width: '150px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '24px' }}> {/* Giữ chiều cao cố định cho label */}
                  <Label for="classSelectModal">Lớp</Label>
                </div>
                <Input
                  type="select"
                  name="classSelect"
                  id="classSelectModal"
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  disabled={isEditing} // Disable khi sửa điểm danh
                >
                  <option value="">Chọn Lớp</option>
                  {classes
                    ? [...classes]
                        .sort((a, b) => a.name.localeCompare(b.name)) // Sắp xếp theo bảng chữ cái
                        .map((cls: any) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.name}
                          </option>
                        ))
                    : []}
                </Input>
              </FormGroup>

              {/* Trường Điểm danh ngày */}
              <FormGroup style={{ width: '180px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '24px' }}>
                  <Label for="attendanceDate">Điểm danh ngày</Label>
                </div>
                <Input
                  type="date"
                  name="attendanceDate"
                  id="attendanceDate"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  // disabled={isEditing}  // Disable khi sửa điểm danh
                />
              </FormGroup>

              {/* Trường Ghi chú */}
              <FormGroup style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: '24px' }}>
                  <Label for="attendanceNote">Ghi Chú</Label>
                </div>
                <Input
                  type="textarea"
                  id="attendanceNote"
                  value={attendanceSessionNote}
                  onChange={(e) => setAttendanceSessionNote(e.target.value)}
                  placeholder="Nhập ghi chú cho buổi điểm danh này..."
                  rows={2}
                />
              </FormGroup>
            </div>
            {isDuplicateSession && !isEditing && ( // Hiển thị thông báo đã có điểm danh rồi
              <div style={{ color: 'red', marginTop: '10px' }}>
                {errorDuplicatedSessionMessage}
              </div>
            )}
            {isScheduleMismatch && !isEditing && ( // Hiển thị thông báo lớp không có lịch học hôm đó
              <div style={{ color: 'red', marginTop: '10px' }}>
                {scheduleMismatchMessage}
              </div>
            )}
            {/* Bảng chọn học sinh */}
            {((!isDuplicateSession && !isScheduleMismatch && !isEditing) || isEditing) && (
            <ScrollableTableWrapper>
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ width: '100px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <Label style={{ display: 'block' }}>Chọn tất cả</Label>
                        <Input
                          type="checkbox"
                          checked={filteredStudents.length > 0 && 
                            selectedStudents.length === (
                              filteredStudents.filter((student: StudentAttributes) => {
                              // Nếu không có startDate -> được xuất hiện trong danh sách sửa
                              if (!student.startDate) return true;
                              // Chuyển cả hai ngày về định dạng YYYY-MM-DD để so sánh chính xác toàn bộ ngày tháng năm
                              return moment(student.startDate).format('YYYY-MM-DD') <= moment(selectedDate).format('YYYY-MM-DD');
                              }).map((s: any) => s.id).length
                            )
                          }
                          onChange={(e) =>
                            setSelectedStudents(
                              e.target.checked ? (
                                filteredStudents.filter((student: StudentAttributes) => {
                                // Nếu không có startDate -> được xuất hiện trong danh sách sửa
                                if (!student.startDate) return true;
                                // Chuyển cả hai ngày về định dạng YYYY-MM-DD để so sánh chính xác toàn bộ ngày tháng năm
                                return moment(student.startDate).format('YYYY-MM-DD') <= moment(selectedDate).format('YYYY-MM-DD');
                                }).map((s: any) => s.id)
                              )
                            : []
                            )
                          }
                        />
                      </div>
                    </th>
                    <th>Tên</th>
                    <th>Lớp</th>
                    <th>Ngày Sinh</th>
                    <th>Ngày Bắt Đầu</th>
                    {userInfo.role === 0 && <th>Ghi chú học sinh</th>}
                    {userInfo.role === 0 && <th>SĐT</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents
                  .filter((student: StudentAttributes) => {
                    // Nếu không có startDate -> được xuất hiện trong danh sách sửa
                    if (!student.startDate) return true;
                    // Chuyển cả hai ngày về định dạng YYYY-MM-DD để so sánh chính xác toàn bộ ngày tháng năm
                    return moment(student.startDate).format('YYYY-MM-DD') <= moment(selectedDate).format('YYYY-MM-DD');
                  })
                  .map((student: StudentAttributes) => {
                    const className = classes ? (classes.find((cls: any) => cls.id === student.classId)?.name || '') : [];
                    // Xác định trạng thái hiển thị trên StatusBar
                    let statusBarText: any = null;
                    if (!student.startDate) {
                      statusBarText = 'Học sinh mới';
                    } else if (moment(student.startDate).format('YYYY-MM-DD') === moment(selectedDate).format('YYYY-MM-DD')) {
                      statusBarText = 'Buổi đầu nhập học';
                    }
                    return (
                      <tr key={student.id} style={ !selectedStudents.includes(student.id) ? { color: 'red' } : {} }>
                        <td>
                          <Input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={(e) => {
                              const updatedSelectedStudents = e.target.checked
                                ? [...selectedStudents, student.id]
                                : selectedStudents.filter((id) => id !== student.id);
                              setSelectedStudents(updatedSelectedStudents);
                            }}
                          />
                        </td>
                        <td>
                        {statusBarText && <StatusBar status={statusBarText} />}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          {student.name}
                          {(student as any).isArchived && (
                            <span style={{ fontSize: '0.65rem', background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '1px 6px', fontWeight: 700, border: '1px solid #ffcc80', whiteSpace: 'nowrap' }}>ĐÃ NGHỈ</span>
                          )}
                        </span>
                      </td>
                        <td>{className}</td>
                        <td>{moment(student.dateOfBirth).locale('vi').format('DD/MM/YYYY')}</td>
                        <td>{moment(student.startDate).locale('vi').format('dddd - DD/MM/YYYY')}</td>
                        {userInfo.role === 0 && <td>{student.note}</td>}
                        {userInfo.role === 0 &&<td>{student.phoneNumber}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </ScrollableTableWrapper>
            )}
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          {((!isDuplicateSession && !isScheduleMismatch && !isEditing) || isEditing) && (
            <div style={{ marginBottom: '15px' }}>
              {(() => {
                const { totalStudents, presentStudents, absentStudents } = calculateAttendanceSummary();
                return (
                  <>
                    <p>
                    <strong style={{ color: 'blue', fontSize: '15px' }}>Đi: {presentStudents}/{totalStudents} - </strong> 
                      <strong style={{ color: 'red', fontSize: '15px' }}>Vắng: {totalStudents - presentStudents} {absentStudents.length > 0 && ` (${absentStudents.join(', ')})`}</strong>
                    </p>
                  </>
                );
              })()}
            </div>
          )}
          <SuccessButton 
            onClick={attendanceSessionToEdit ? handleSaveAttendance : handleCheckNewStudents}
            disabled={
              !selectedClass || // Disable nếu chưa chọn lớp
              ((isDuplicateSession || isScheduleMismatch) && !isEditing) || 
              isCreatingAttendance
            }
            style={{
              opacity: isCreatingAttendance ? 0.6 : 1,
              cursor: isCreatingAttendance ? 'not-allowed' : 'pointer',
            }}
          >
            {isCreatingAttendance ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {attendanceSessionToEdit ? 'Đang lưu...' : 'Đang tạo...'}
              </>
            ) : (
              attendanceSessionToEdit ? 'Sửa' : 'Tạo'
            )}
          </SuccessButton>
          <SecondaryButton onClick={toggleModal}>
            Hủy
          </SecondaryButton>
        </ModalFooter>
      </StyledModal>
      {showDeletePopup && sessionToDelete && (() => {
        const className = classes.find((cls: any) => cls.id === sessionToDelete.classId)?.name || `ID ${sessionToDelete.classId}`;
        const sessionDate = moment(sessionToDelete.date).locale('vi');
        const dayLabel = sessionDate.format('dddd'); // Thứ Hai, Thứ Ba...
        const dateLabel = sessionDate.format('DD/MM/YYYY');
        return (
          <ConfirmationPopup
            isOpen={showDeletePopup}
            onClose={() => setShowDeletePopup(false)}
            title="🗑️ Xác nhận xoá điểm danh"
            message={
              <div>
                {/* Info card */}
                <div style={{
                  background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                  border: '1.5px solid #ffb74d',
                  borderRadius: 12,
                  padding: '14px 18px',
                  marginBottom: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.6rem' }}>📚</span>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#e65100', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Lớp học</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#bf360c' }}>{className}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.6rem' }}>📅</span>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#e65100', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Ngày điểm danh</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#bf360c' }}>
                        <span style={{ textTransform: 'capitalize' }}>{dayLabel}</span>
                        {' — '}{dateLabel}
                      </div>
                    </div>
                  </div>
                </div>

                {userInfo.role == 0 ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fce4ec', borderRadius: 8, padding: '10px 14px', border: '1px solid #ef9a9a' }}>
                    <span style={{ fontSize: '1.2rem', marginTop: 1 }}>⚠️</span>
                    <span style={{ fontSize: '0.9rem', color: '#c62828', fontWeight: 500 }}>
                      Hành động này <strong>không thể hoàn tác</strong>. Toàn bộ dữ liệu điểm danh buổi này sẽ bị xoá vĩnh viễn.
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#e3f2fd', borderRadius: 8, padding: '10px 14px', border: '1px solid #90caf9' }}>
                    <span style={{ fontSize: '1.2rem' }}>🔒</span>
                    <span style={{ fontSize: '0.9rem', color: '#1565c0', fontWeight: 500 }}>
                      Chỉ <strong>ADMIN</strong> có thể xoá điểm danh. Liên hệ Admin để thực hiện.
                    </span>
                  </div>
                )}
              </div>
            }
            onConfirm={() => {
              userInfo.role == 0 ? handleDeleteAttendance(sessionToDelete) : setShowDeletePopup(false);
            }}
          />
        );
      })()}
      <ConfirmationPopup
        isOpen={showConfirmNewStudents}
        onClose={() => setShowConfirmNewStudents(false)}
        title={`Xác nhận có học sinh mới`}
        message={
          <>
            Bạn có chắc chắn hôm nay có <strong>{newStudentsList.length}</strong> học sinh mới đi buổi đầu tiên không? <br/>
            {newStudentsList.map(student => (
              <div key={student.id}><strong>{student.name}</strong></div>
            ))}
          </>
        }
        onConfirm={() => {
          setShowConfirmNewStudents(false);
          handleCreateAttendance();
        }}
      />
    </AttendanceContainer>
  );
};

export default AttendanceManage;
